import { useState } from "react";
import { DateTime } from "luxon";
import {
  clampDateToPeriod,
  clampPeriodRange,
  formatPeriodLabel,
  formatSalesDateLabel,
  getDefaultPeriodRange,
  getDefaultSelectedDateInPeriod,
  getPeriodDayCount,
  getPeriodRangeForPreset,
  getSalesToday,
  SALES_TIMEZONE,
  type SalesPeriodPreset,
} from "../utils/dailySalesUtils";

const parseDate = (date: string) =>
  DateTime.fromISO(date, { zone: SALES_TIMEZONE }).startOf("day");

export const useSalesPeriodState = () => {
  const [periodPreset, setPeriodPreset] =
    useState<SalesPeriodPreset>("last30Days");
  const [period, setPeriod] = useState(getDefaultPeriodRange);
  const [selectedDate, setSelectedDate] = useState(() =>
    getDefaultSelectedDateInPeriod(period.from, period.to)
  );

  const applyPeriod = (nextPeriod: { from: string; to: string }) => {
    setPeriod(nextPeriod);
    setSelectedDate(
      getDefaultSelectedDateInPeriod(nextPeriod.from, nextPeriod.to)
    );
  };

  const handlePeriodChange = (from: string, to: string) => {
    setPeriodPreset("custom");
    applyPeriod(clampPeriodRange(from, to));
  };

  const handlePresetChange = (preset: SalesPeriodPreset) => {
    if (preset === "custom") {
      setPeriodPreset("custom");
      return;
    }
    setPeriodPreset(preset);
    applyPeriod(getPeriodRangeForPreset(preset));
  };

  const handlePrevPeriod = () => {
    const start = parseDate(period.from);
    const end = parseDate(period.to);
    const shift = getPeriodDayCount(period.from, period.to);
    handlePeriodChange(
      start.minus({ days: shift }).toFormat("yyyy-MM-dd"),
      end.minus({ days: shift }).toFormat("yyyy-MM-dd")
    );
  };

  const handleNextPeriod = () => {
    const today = getSalesToday();
    const end = parseDate(period.to);
    const shift = getPeriodDayCount(period.from, period.to);
    let newEnd = end.plus({ days: shift });
    if (newEnd > today) {
      newEnd = today;
    }
    const newStart = newEnd.minus({ days: shift - 1 });
    handlePeriodChange(
      newStart.toFormat("yyyy-MM-dd"),
      newEnd.toFormat("yyyy-MM-dd")
    );
  };

  const handleSelectDate = (date: string) => {
    setSelectedDate(clampDateToPeriod(date, period.from, period.to));
  };

  const periodLabel = formatPeriodLabel(period.from, period.to);
  const selectedDateLabel = formatSalesDateLabel(selectedDate);
  const canGoForward = period.to < getSalesToday().toFormat("yyyy-MM-dd");

  return {
    period,
    periodPreset,
    periodLabel,
    selectedDate,
    selectedDateLabel,
    canGoForward,
    handlePeriodChange,
    handlePresetChange,
    handlePrevPeriod,
    handleNextPeriod,
    handleSelectDate,
  };
};

export type SalesPeriodState = ReturnType<typeof useSalesPeriodState>;
